import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import theme from '../../theme';
import Topbar from '../Topbar';
import { withLanguage } from '../common/Texts';
import { isHighPotential } from '../../utils/potential';
import useLabelData from './useLabelData';
import useAIClassification from './useAIClassification';
import useAISpecOptions from './useAISpecOptions';
import EmployeeDataCard from './EmployeeDataCard';
import SurveyResponsesCard from './SurveyResponsesCard';
import LabelingCard from './LabelingCard';
import AIClassificationCard from './AIClassificationCard';
import PossibleCurvesCard from './PossibleCurvesCard';

export default function Label() {
    const { id } = useParams();
    const navigate = useNavigate();
    const language = useSelector(s => s.user.language);
    const isStaff = useSelector(s => s.user.is_staff);

    const tr = useMemo(() => withLanguage(language), [language]);

    const data = useLabelData(id, language, navigate);
    const ai = useAIClassification(isStaff ? id : null);
    const specOptions = useAISpecOptions(isStaff);

    // Con qué spec se va a correr la próxima clasificación. Se deriva en vez de
    // sincronizarse con un efecto: mientras el operador no elija nada se propone
    // el de la última corrida de esta encuesta, que es lo que casi siempre se
    // quiere repetir. Sin corridas previas se propone el default del servidor.
    const [specOverride, setSpecOverride] = useState(null);
    // Cada sugerencia se usa sólo si el servicio la sigue listando: un spec que
    // quedó fuera del catálogo dejaría el selector con un valor sin opción y el
    // botón habilitado para una corrida que el backend rechaza.
    const isUsable = (name) => Boolean(name) && specOptions.specs.some((s) => s.name === name);
    const lastSpec = ai.classifications[0]?.spec_name ?? '';
    const suggestedSpec = [lastSpec, specOptions.defaultSpec].find(isUsable) ?? '';
    const selectedSpec = specOverride ?? suggestedSpec;

    // Nivel sobre el que se dibujan las curvas posibles: el etiquetado en curso
    // (así el gráfico reacciona a lo que el staff elige en el momento) y, si aún
    // no hay etiqueta completa, el calculado por el sistema.
    const curveLevel = useMemo(() => {
        if (data.nivelLabel !== '' && data.subnivelLabel !== '') {
            return { level: data.nivelLabel, sublevel: data.subnivelLabel };
        }
        return { level: data.nivelCalc, sublevel: data.subnivelCalc };
    }, [data.nivelLabel, data.subnivelLabel, data.nivelCalc, data.subnivelCalc]);

    const highPot = useMemo(() => {
        if (!data.replyData) return false;
        return isHighPotential(data.replyData, undefined, language);
    }, [data.replyData, language]);

    if (!data.loaded) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Topbar />
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
                    <CircularProgress />
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Topbar />

            <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 8 }}>
                <Container maxWidth="lg" sx={{ pt: 4 }}>

                    {/* Breadcrumb */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <Typography
                            component={Link}
                            to="/results"
                            sx={{
                                fontSize: '13px',
                                color: 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': { color: 'primary.main' },
                            }}
                        >
                            {tr('results') || 'Results'}
                        </Typography>
                        <NavigateNextIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography sx={{ fontSize: '13px', color: 'text.primary', fontWeight: 500 }}>
                            {data.user_generic_id || `#${id}`}
                        </Typography>
                    </Box>

                    {/* Encabezado de página */}
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, fontSize: '26px' }}>
                        {tr('survey_of')} {data.company.name}
                    </Typography>

                    <Divider sx={{ my: 2.5 }} />

                    {/* Tarjetas */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <EmployeeDataCard
                            tr={tr}
                            language={language}
                            questionnaire={data.questionnaire}
                            isHighPot={highPot}
                            nombre={data.nombre}
                            dob={data.dob}
                            user_generic_id={data.user_generic_id}
                            id_label={data.id_label}
                            posicion={data.posicion}
                            areafunc={data.areafunc}
                            business_unit={data.business_unit}
                            grupo_interno={data.grupo_interno}
                            nivelCalc={data.nivelCalc}
                            subnivelCalc={data.subnivelCalc}
                            nivelLabel={data.nivelLabel}
                            subnivelLabel={data.subnivelLabel}
                            nivelPot={data.nivelPot}
                            duration_seconds={data.duration_seconds}
                            status={data.status}
                            performance={data.performance}
                            positions={data.positions}
                            departments={data.departments}
                            business_units={data.business_units}
                            grupos_internos={data.grupos_internos}
                            dynamic_fields={data.dynamic_fields}
                            dynamic_answers={data.dynamic_answers}
                            dynamic_errors={data.dynamic_errors}
                            generic_id_error={data.generic_id_error}
                            handleChange={data.handleChange}
                            handleDynamicChange={data.handleDynamicChange}
                        />

                        {isStaff && (
                            <SurveyResponsesCard
                                tr={tr}
                                language={language}
                                questions={data.questions}
                                answers={data.answers}
                            />
                        )}

                        {isStaff && (
                            <LabelingCard
                                tr={tr}
                                nivelLabel={data.nivelLabel}
                                subnivelLabel={data.subnivelLabel}
                                invalid={data.invalid}
                                published={data.published}
                                handleChange={data.handleChange}
                                handleToggle={data.handleToggle}
                                alternativeLabels={data.alternativeLabels}
                                alternativeErrors={data.alternativeErrors}
                                addAlternative={data.addAlternative}
                                changeAlternative={data.changeAlternative}
                                removeAlternative={data.removeAlternative}
                            />
                        )}

                        {isStaff && (
                            <AIClassificationCard
                                tr={tr}
                                classification={ai.classification}
                                classify={ai.classify}
                                starting={ai.starting}
                                error={ai.error}
                                timedOut={ai.timedOut}
                                pending={ai.pending}
                                errorDetail={ai.errorDetail}
                                classifications={ai.classifications}
                                specs={specOptions.specs}
                                specsError={specOptions.error}
                                selectedSpec={selectedSpec}
                                onSelectSpec={setSpecOverride}
                                nivelLabel={data.nivelLabel}
                                subnivelLabel={data.subnivelLabel}
                                alternativeLabels={data.alternativeLabels}
                            />
                        )}

                        {isStaff && (
                            <PossibleCurvesCard
                                tr={tr}
                                language={language}
                                companyChart={data.company.chart}
                                level={curveLevel.level}
                                sublevel={curveLevel.sublevel}
                                age={data.age_at_reply}
                                createdDate={data.created_date}
                            />
                        )}
                    </Box>

                    {/* Botones de acción */}
                    {data.save_error && (
                        <Typography role="alert" sx={{ mt: 3, color: 'error.main', fontSize: '14px', textAlign: 'right' }}>
                            {data.save_error}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, mt: data.save_error ? 1 : 3, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            sx={{ color: 'text.secondary', borderColor: '#E1E4E8' }}
                            onClick={() => navigate('/results')}
                        >
                            {tr('cancel')}
                        </Button>

                        {isStaff && (
                            <Button
                                variant="outlined"
                                startIcon={<DescriptionOutlinedIcon />}
                                onClick={() => navigate(`/report/l/${id}`)}
                            >
                                {tr('report')}
                            </Button>
                        )}

                        {isStaff ? (
                            <Button
                                variant="contained"
                                startIcon={<LabelOutlinedIcon />}
                                onClick={() => data.save(true)}
                            >
                                {tr('to_label')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={<SaveOutlinedIcon />}
                                onClick={() => data.save(false)}
                            >
                                {tr('record')}
                            </Button>
                        )}
                    </Box>

                </Container>
            </Box>
        </ThemeProvider>
    );
}
